import React, { useEffect, useRef, useState } from "react";
import { Image, Textbox } from "fabric";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const textColorOptions = [
  { label: "Czarny", value: "#000" },
  { label: "Biały", value: "#fff" },
  { label: "Złoty", value: "#C8A044" },
  { label: "Granatowy", value: "#1C3F58" },
];

function getObjectSnapshot(object) {
  if (!object) return null;

  const baseSnapshot = {
    left: object.left,
    top: object.top,
    scaleX: object.scaleX,
    scaleY: object.scaleY,
    angle: object.angle,
    width: object.width,
    height: object.height,
  };

  if (object.type === "textbox") {
    return {
      ...baseSnapshot,
      text: object.text,
      fontSize: object.fontSize,
      fontFamily: object.fontFamily,
      fontWeight: object.fontWeight,
      fontStyle: object.fontStyle,
      underline: object.underline,
      fill: object.fill,
      textAlign: object.textAlign,
      styles: object.styles,
    };
  }

  return baseSnapshot;
}

function Settings({
  canvas,
  selectedObject,
  defaultTextColor,
  isOpen = true,
  isMobile = false,
  onClose,
  onHistoryCommit,
  onUndo,
  onRedo,
  onResetLayout,
  canUndo = false,
  canRedo = false,
}) {
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [fontSize, setFontSize] = useState("");
  const [fontFamily, setFontFamily] = useState("");
  const [color, setColor] = useState("");
  const selectedBaselineRef = useRef(null);
  const selectedObjectRef = useRef(null);

  const isCanvasReady = Boolean(canvas);
  const selectedLabel =
    selectedObject?.elementLabel ||
    (selectedObject?.type === "textbox" ? "Tekst" : "Obraz");

  const commitCanvasChange = () => {
    canvas?.renderAll();
    onHistoryCommit?.();
  };

  useEffect(() => {
    if (!selectedObject) {
      clearSettings();
      selectedBaselineRef.current = null;
      selectedObjectRef.current = null;
      return;
    }

    if (selectedObjectRef.current !== selectedObject) {
      selectedBaselineRef.current = getObjectSnapshot(selectedObject);
      selectedObjectRef.current = selectedObject;
    }

    if (selectedObject.type === "textbox") {
      setWidth("");
      setHeight("");
      setFontSize(selectedObject.fontSize ?? "");
      setFontFamily(selectedObject.fontFamily ?? "");
      setColor(selectedObject.fill || "#000");
    } else if (selectedObject.type === "image") {
      setFontSize("");
      setFontFamily("");
      setColor("");
      setWidth(Math.round(selectedObject.width * selectedObject.scaleX));
      setHeight(Math.round(selectedObject.height * selectedObject.scaleY));
    }
  }, [selectedObject]);

  const clearSettings = () => {
    setWidth("");
    setHeight("");
    setFontSize("");
    setFontFamily("");
    setColor("");
  };

  const handleWidthChange = (e) => {
    const value = e.target.value.replace(/,/g, "");
    const intValue = parseInt(value, 10);

    setWidth(Number.isNaN(intValue) ? "" : intValue);
    if (!selectedObject || Number.isNaN(intValue) || intValue < 0) return;

    if (selectedObject.type === "textbox") {
      selectedObject.set({ width: intValue / selectedObject.scaleX });
      selectedObject.initDimensions();
    } else if (selectedObject.type === "image") {
      selectedObject.set({ scaleX: intValue / selectedObject.width });
    }

    commitCanvasChange();
  };

  const handleHeightChange = (e) => {
    const value = e.target.value.replace(/,/g, "");
    const intValue = parseInt(value, 10);

    setHeight(Number.isNaN(intValue) ? "" : intValue);
    if (!selectedObject || Number.isNaN(intValue) || intValue < 0) return;

    if (selectedObject.type === "textbox") {
      selectedObject.set({ height: intValue / selectedObject.scaleY });
    } else if (selectedObject.type === "image") {
      selectedObject.set({ scaleY: intValue / selectedObject.height });
    }

    commitCanvasChange();
  };

  const handleFontSizeChange = (e) => {
    const value = e.target.value.replace(/,/g, "");
    const intValue = parseInt(value, 10);

    setFontSize(Number.isNaN(intValue) ? "" : intValue);
    if (!selectedObject || Number.isNaN(intValue) || intValue < 0) return;

    selectedObject.set({ fontSize: intValue });
    commitCanvasChange();
  };

  const handleItalicChange = () => {
    if (!selectedObject || selectedObject.type !== "textbox") return;

    const selectionStart = selectedObject.selectionStart;
    const selectionEnd = selectedObject.selectionEnd;

    if (selectionStart === selectionEnd) {
      const newValue = selectedObject.fontStyle === "italic" ? "normal" : "italic";
      selectedObject.set({ fontStyle: newValue });
      for (let i = 0; i < selectedObject.text.length; i += 1) {
        selectedObject.setSelectionStyles({ fontStyle: newValue }, i, i + 1);
      }
    } else {
      const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
      const currentStyle =
        styles.length > 0 && styles[0].fontStyle === "italic" ? "italic" : "normal";
      const newStyle = currentStyle === "italic" ? "normal" : "italic";

      for (let i = selectionStart; i < selectionEnd; i += 1) {
        selectedObject.setSelectionStyles({ fontStyle: newStyle }, i, i + 1);
      }
    }

    selectedObject.set({ dirty: true });
    commitCanvasChange();
  };

  const handleUnderlineChange = () => {
    if (!selectedObject || selectedObject.type !== "textbox") return;

    const selectionStart = selectedObject.selectionStart;
    const selectionEnd = selectedObject.selectionEnd;

    if (selectionStart === selectionEnd) {
      const newValue = !selectedObject.underline;
      selectedObject.set({ underline: newValue });
      for (let i = 0; i < selectedObject.text.length; i += 1) {
        selectedObject.setSelectionStyles({ underline: newValue }, i, i + 1);
      }
    } else {
      const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
      const firstStyle = styles[0] || {};
      const newValue = !firstStyle.underline;

      for (let i = selectionStart; i < selectionEnd; i += 1) {
        selectedObject.setSelectionStyles({ underline: newValue }, i, i + 1);
      }
    }

    selectedObject.set({ dirty: true });
    commitCanvasChange();
  };

  const handleFontFamilyChange = (e) => {
    const newFontFamily = e.target.value;

    setFontFamily(newFontFamily);
    if (!selectedObject || !newFontFamily) return;

    selectedObject.set({ fontFamily: newFontFamily });
    commitCanvasChange();
  };

  const handleTextAlignChange = (value) => {
    if (!selectedObject || !value) return;

    selectedObject.set({ textAlign: value });
    commitCanvasChange();
  };

  const handleRemoveSelected = () => {
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      onHistoryCommit?.();
    }
  };

  const handleAddText = () => {
    if (!canvas) return;

    const newText = new Textbox("Nowy napis", {
      left: 50,
      top: 330,
      fontSize: 50,
      textAlign: "center",
      width: 300,
      fontWeight: 300,
      fill: defaultTextColor,
    });

    canvas.add(newText);
    canvas.setActiveObject(newText);
    commitCanvasChange();
  };

  const handleAddImage = (event) => {
    if (!canvas) return;

    const file = event.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    const imageElement = document.createElement("img");
    imageElement.src = url;
    imageElement.crossOrigin = "anonymous";

    imageElement.onload = () => {
      const imageWidth = imageElement.width;
      const imageHeight = imageElement.height;
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const maxImageWidth = canvasWidth * 0.5;

      const scale = Math.min(maxImageWidth / imageWidth, canvasHeight / imageHeight);

      const fabricImage = new Image(imageElement, {
        top: 0,
        left: 0,
        scaleX: scale,
        scaleY: scale,
      });

      canvas.add(fabricImage);
      canvas.setActiveObject(fabricImage);
      commitCanvasChange();
      URL.revokeObjectURL(url);
    };
  };

  const handleBoldSelection = () => {
    if (!selectedObject || selectedObject.type !== "textbox") return;

    const selectionStart = selectedObject.selectionStart;
    const selectionEnd = selectedObject.selectionEnd;

    if (selectionStart === selectionEnd) {
      const newWeight = selectedObject.fontWeight === "bold" ? "normal" : "bold";
      selectedObject.set({ fontWeight: newWeight });
      for (let i = 0; i < selectedObject.text.length; i += 1) {
        selectedObject.setSelectionStyles({ fontWeight: newWeight }, i, i + 1);
      }
    } else {
      const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
      const currentWeight =
        styles.length > 0 && styles[0].fontWeight === "bold" ? "bold" : "normal";
      const newWeight = currentWeight === "bold" ? "normal" : "bold";

      for (let i = selectionStart; i < selectionEnd; i += 1) {
        selectedObject.setSelectionStyles({ fontWeight: newWeight }, i, i + 1);
      }
    }

    selectedObject.set({ dirty: true });
    commitCanvasChange();
  };

  const updateTextColor = (newColor) => {
    setColor(newColor);
    if (!selectedObject || selectedObject.type !== "textbox") return;

    selectedObject.set({ fill: newColor });
    commitCanvasChange();
  };

  const handleResetSelected = () => {
    if (!selectedObject || !selectedBaselineRef.current) return;

    selectedObject.set(selectedBaselineRef.current);
    if (selectedObject.type === "textbox") {
      selectedObject.initDimensions();
    }
    commitCanvasChange();
  };

  return (
    <aside
      className={`settingsPanel${isMobile ? " mobile" : ""}${isMobile && isOpen ? " open" : ""}`}
    >
      <div className="settingsPanelHeader">
        <div>
          <span className="settingsEyebrow">Narzędzia</span>
          <h2>{selectedObject ? selectedLabel : "Ustawienia projektu"}</h2>
        </div>
        {isMobile && (
          <button className="customBtn mobileCloseBtn" onClick={onClose}>
            Zamknij
          </button>
        )}
      </div>

      <div className="settingsQuickActions">
        <button className="customBtn secondary" onClick={onUndo} disabled={!canUndo}>
          <UndoIcon fontSize="small" />
          Cofnij
        </button>
        <button className="customBtn secondary" onClick={onRedo} disabled={!canRedo}>
          <RedoIcon fontSize="small" />
          Ponów
        </button>
      </div>

      <div className="settingsSection">
        <button className="customBtn" onClick={handleAddText} disabled={!isCanvasReady}>
          Dodaj tekst
        </button>
        <input
          type="file"
          accept="image/*"
          id="fileInput"
          className="inputFile"
          onChange={handleAddImage}
          disabled={!isCanvasReady}
        />
        <label
          htmlFor="fileInput"
          className={`customBtn${!isCanvasReady ? " disabled" : ""}`}
        >
          Dodaj obraz
        </label>
      </div>

      {!selectedObject && (
        <div className="settingsEmptyState">
          <strong>Zaznacz element na projekcie.</strong>
          <span>Po kliknięciu tekstu lub obrazu pojawią się szybkie opcje edycji.</span>
        </div>
      )}

      {selectedObject && selectedObject.type === "textbox" && (
        <div className="settingsSection">
          <label>Rozmiar czcionki</label>
          <input type="number" value={fontSize} onChange={handleFontSizeChange} />

          <label>Czcionka</label>
          <select
            name="fontFamily"
            id="fontFamily"
            onChange={handleFontFamilyChange}
            value={fontFamily}
            className="selectFont"
          >
            <option value="Arial" style={{ fontFamily: "Arial" }}>Arial</option>
            <option value="Helvetica" style={{ fontFamily: "Helvetica" }}>Helvetica</option>
            <option value="Times New Roman" style={{ fontFamily: "Times New Roman" }}>
              Times New Roman
            </option>
            <option value="Courier New" style={{ fontFamily: "Courier New" }}>
              Courier New
            </option>
            <option value="Bitter" style={{ fontFamily: "Bitter" }}>Bitter</option>
            <option value="DM Serif Display" style={{ fontFamily: "DM Serif Display" }}>
              DM Serif Display
            </option>
            <option value="Verdana" style={{ fontFamily: "Verdana" }}>Verdana</option>
            <option value="Georgia" style={{ fontFamily: "Georgia" }}>Georgia</option>
            <option value="monospace" style={{ fontFamily: "monospace" }}>Monospace</option>
            <option value="cursive" style={{ fontFamily: "cursive" }}>Cursive</option>
            <option value="fantasy" style={{ fontFamily: "fantasy" }}>Fantasy</option>
          </select>

          <label>Kolor tekstu</label>
          <div className="colorPickerGrid" role="radiogroup" aria-label="Kolor tekstu">
            {textColorOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`colorPickerOption${color === option.value ? " active" : ""}`}
                onClick={() => updateTextColor(option.value)}
                aria-pressed={color === option.value}
              >
                <span
                  className="colorPickerSwatch"
                  style={{ backgroundColor: option.value }}
                />
                <span>{option.label}</span>
              </button>
            ))}
          </div>

          <div className="btnsWrapperThree">
            <IconButton color="primary" size="large" onClick={() => handleTextAlignChange("left")} className="btnWrapperDel">
              <FormatAlignLeftIcon fontSize="inherit" color="disabled" className="icon" />
            </IconButton>
            <IconButton color="primary" size="large" onClick={() => handleTextAlignChange("center")} className="btnWrapperDel">
              <FormatAlignCenterIcon fontSize="inherit" color="disabled" className="icon" />
            </IconButton>
            <IconButton color="primary" size="large" onClick={() => handleTextAlignChange("right")} className="btnWrapperDel">
              <FormatAlignRightIcon fontSize="inherit" color="disabled" className="icon" />
            </IconButton>
            <IconButton className="btnWrapperDel" color="primary" onClick={handleBoldSelection}>
              <FormatBoldIcon fontSize="inherit" color="disabled" className="icon" />
            </IconButton>
            <IconButton className="btnWrapperDel" color="primary" onClick={handleItalicChange}>
              <FormatItalicIcon fontSize="inherit" color="disabled" className="icon" />
            </IconButton>
            <IconButton className="btnWrapperDel" color="primary" onClick={handleUnderlineChange}>
              <FormatUnderlinedIcon fontSize="inherit" color="disabled" className="icon" />
            </IconButton>
          </div>
        </div>
      )}

      {selectedObject && selectedObject.type === "image" && (
        <div className="settingsSection">
          <label>Szerokość</label>
          <input type="number" value={width} onChange={handleWidthChange} />
          <label>Wysokość</label>
          <input type="number" value={height} onChange={handleHeightChange} />
        </div>
      )}

      <div className="settingsSection settingsDangerZone">
        {selectedObject && (
          <>
            <button className="customBtn secondary" onClick={handleResetSelected}>
              <RestartAltIcon fontSize="small" />
              Resetuj element
            </button>
            <button className="customBtn secondary danger" onClick={handleRemoveSelected}>
              <DeleteIcon fontSize="small" />
              Usuń element
            </button>
          </>
        )}
        <button className="customBtn secondary" onClick={onResetLayout} disabled={!isCanvasReady}>
          Resetuj układ
        </button>
      </div>
    </aside>
  );
}

export default Settings;
